import { IconButton } from '@attendance/ui/components/IconButton';
import { Globe, Monitor, Moon, Sun } from 'lucide-react';
import { type ThemeMode } from '../../stores/themeStore.js';

/**
 * Three-state theme toggle (light → dark → system → light…). Renders the
 * icon for the user's *currently selected* preference rather than the
 * resolved theme — so a user on "system" sees the Monitor icon even when
 * the OS resolves it to dark. Click cycles through; keyboard users get the
 * same affordance via the button's accessible label.
 */
export function ThemeToggle({
  theme,
  onCycle,
}: {
  theme: ThemeMode;
  onCycle: () => void;
}): JSX.Element {
  const Icon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;
  const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
  return (
    <IconButton
      onClick={onCycle}
      label={`Theme: ${theme}. Click to switch to ${next}.`}
    >
      <Icon size={18} />
    </IconButton>
  );
}

/**
 * Two-state language toggle (English ↔ Arabic). The kiosk + admin shells
 * support these two today; the i18n config in `lib/i18n.ts` enumerates the
 * supported set, so adding a third locale only requires expanding both.
 * Flips `<html dir>` to rtl via the `languageChanged` listener in main.tsx.
 */
export function LanguageToggle({
  currentLang,
  onChange,
  label,
}: {
  currentLang: string;
  onChange: (lng: string) => void;
  label: string;
}): JSX.Element {
  // Match by language subtag so 'ar-AE' / 'en-US' both work.
  const lang = currentLang.split('-')[0] ?? 'en';
  const next = lang === 'ar' ? 'en' : 'ar';
  return (
    <IconButton
      onClick={() => onChange(next)}
      label={`${label}: ${lang.toUpperCase()}. Click to switch to ${next.toUpperCase()}.`}
      // The language code rides along with the globe, so this one is not
      // strictly icon-only and takes its natural width.
      adornment={<span className="text-label uppercase">{lang}</span>}
    >
      <Globe size={18} />
    </IconButton>
  );
}
